import {
  ColorScheme,
  LOADER_UI_PREFERENCES_ID,
} from '@inway/meteor-layout-helper';
import { useDebugValue, useEffect, useLayoutEffect, useState } from 'react';

/**
 * A React hook that retrieves and observes the user's preferred color scheme.
 *
 * This hook reads the color scheme preference from a designated element in the DOM with the
 * ID specified by `LOADER_UI_PREFERENCES_ID`. It also sets up a MutationObserver to detect
 * any changes to the color scheme preference and updates the state accordingly.
 *
 * @returns {ColorScheme} The current preferred color scheme ('light', 'dark', or 'auto').
 *
 * @example
 * ```tsx
 * function App() {
 *   const colorScheme = usePreferredColorScheme();
 *
 *   return (
 *     <div className={`app ${colorScheme}`}>
 *     </div>
 *   );
 * }
 * ```
 */
export default function usePreferredColorScheme(): ColorScheme {
  const [preferredColorScheme, setPreferredColorScheme] =
    useState<ColorScheme>('auto');

  useLayoutEffect(() => {
    const savedColorScheme =
      (document.getElementById(LOADER_UI_PREFERENCES_ID)?.dataset
        ?.colorScheme as ColorScheme) ?? 'auto';

    if (savedColorScheme !== preferredColorScheme)
      setPreferredColorScheme(savedColorScheme);
  }, []);

  useEffect(() => {
    const loaderUiPreferences = document.getElementById(
      LOADER_UI_PREFERENCES_ID
    );
    if (loaderUiPreferences == null) {
      return;
    }

    /**
     * Mutation observer for detecting changes in the loader UI preferences element.
     */
    const observer = new MutationObserver(() =>
      setPreferredColorScheme(
        (loaderUiPreferences?.dataset?.colorScheme ?? 'auto') as ColorScheme
      )
    );
    observer.observe(loaderUiPreferences, {
      attributes: true,
      attributeFilter: ['data-color-scheme'],
    });

    return () => {
      observer.disconnect();
    };
  }, [preferredColorScheme]);

  useDebugValue(preferredColorScheme);

  return preferredColorScheme;
}
